import React, { useEffect, useState } from "react";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import { GET_BIRTHDAY_CONFIG, SAVE_BIRTHDAY } from "../../../api/apiRoutes";
import useFacility from "../../../../utils/hooks/useFacility";
import { BirthDayConfig } from "../../../models/BirthDaysModels/BirthDayConfig";
import { BirthdayAssortWithSteps } from "../../../models/BirthDaysModels/BirthdayAssortWithSteps";
import { BirthDayTimeAndQnt, initialBirthDayTimeAndQnt } from "../../../models/BirthDaysModels/BirthDayTimeAndQnt";
import { BirthDayDetails, initialBirthDayDetails } from "../../../models/BirthDaysModels/BirthDayDetails";
import { BasketItem } from "../../../models/BasketItem";
import getId from "../../../../utils/getId";
import { useDispatch } from "react-redux";
import { setSuccessMessage } from "../../../store/alerts/alertsActions";
import { SaveBirhtDays } from "../../../models/BirthDaysModels/SaveBirhtDays";
import { Assort } from "../../../models/Assort";
import { useTranslation } from "react-i18next";

export default function useBirthdayManager() {
  const { t } = useTranslation();
  const { get, post } = useApiRequest();
  const dispatch = useDispatch();
  const facilityIds = useFacility();
  const [data, setData] = useState<BirthDayConfig>({} as BirthDayConfig);
  const [assortFirstStep, setAssortFirstStep] = useState<BirthdayAssortWithSteps>({} as BirthdayAssortWithSteps);
  const [birthDayTime, setBirthDayTime] = useState<BirthDayTimeAndQnt>(initialBirthDayTimeAndQnt);
  const [birthDayDetails, setBirthDayDetails] = useState<BirthDayDetails>(initialBirthDayDetails);
  const [lines, setLines] = useState<BasketItem[]>([]);
  const [activeStepThree, setActiveStepThree] = useState(false);
  const [activeStepsAnother, setActiveStepsAnother] = useState(false);

  const getBirthDayConfig = async () => {
    const { result, isError } = await get(GET_BIRTHDAY_CONFIG(facilityIds));
    if (!isError) {
      setData(result);
    }
  };

  const isAssortInBasket = (assort: Assort) => {
    return lines.some((item: BasketItem) => item.assortIds === getId(assort));
  };

  const onAddLines = (assort: Assort, qnt: number) => {
    if (isAssortInBasket(assort)) {
      setLines(lines.map((item) => (item.assortIds === getId(assort) ? { ...item, qnt: item.qnt + qnt } : item)));
    } else {
      setLines([setBasketItem(assort), ...lines]);
    }
  };

  const setBasketItem = (assort: Assort) => {
    return {
      assortIds: getId(assort),
      facilityIds,
      assortName: assort.name,
      price: assort.price,
      qnt: 1,
    } as BasketItem;
  };

  const onSelectLineAndDeleteOthers = (assort: Assort, assortsToDelete: Assort[]) => {
    const newAssort = setBasketItem(assort);
    const filteredLines = lines.filter((item1) => !assortsToDelete.some((item2) => getId(item2) === item1.assortIds));
    setLines([newAssort, ...filteredLines]);
  };

  useEffect(() => {
    const filteredLines = lines.filter((item) => item.qnt > 0);
    if (filteredLines.length !== lines.length) {
      setLines(filteredLines);
    }
  }, [lines, setLines]);

  const clearReservation = () => {
    setActiveStepThree(false);
    setActiveStepsAnother(false);
    setAssortFirstStep({} as BirthdayAssortWithSteps);
    setBirthDayTime(initialBirthDayTimeAndQnt);
    setBirthDayDetails(initialBirthDayDetails);
    setLines([]);
  };

  const saveBithDayReservation = async () => {
    const params = {
      mainAssortIds: getId(assortFirstStep),
      dateFrom: `${birthDayTime.date} ${birthDayTime.time}:00`,
      dateTo: `${birthDayTime.date} ${birthDayTime.time}:00`,
      peopleAmount: birthDayTime.qnt,
      lines,
      title: birthDayDetails.additionalInfo,
    } as SaveBirhtDays;

    const { isError } = await post(
      SAVE_BIRTHDAY(
        facilityIds,
        birthDayDetails.customerName,
        birthDayDetails.customerLastName,
        birthDayDetails.email,
        birthDayDetails.phone
      ),
      params
    );

    if (!isError) {
      dispatch(setSuccessMessage(t("poprawnie_przeslano_formularz_rezerwacji")));
      clearReservation();
    }
  };

  React.useEffect(() => {
    if (Boolean(facilityIds)) {
      getBirthDayConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityIds]);

  return {
    birthDayData: data,
    firstStepMethod: {
      assortFirstStep,
      setAssortFirstStep,
    },
    secondStepMethod: {
      birthDayTime,
      setBirthDayTime,
      activeStepThree,
      setActiveStepThree,
    },
    thirdStepMethod: {
      birthDayDetails,
      setBirthDayDetails,
      activeStepsAnother,
      setActiveStepsAnother,
    },
    lines,
    onAddLines,
    clearReservation,
    saveBithDayReservation,
    onSelectLineAndDeleteOthers,
  };
}
